import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Localization from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';

import enCommon from '@/assets/locales/en/common.json';
import enZod from 'zod-i18n-map/locales/en/zod.json'

import deCommon from '@/assets/locales/de/common.json';
import deZod from 'zod-i18n-map/locales/de/zod.json'

import frCommon from '@/assets/locales/fr/common.json';
import frZod from 'zod-i18n-map/locales/fr/zod.json'

import itCommon from '@/assets/locales/it/common.json';
import itZod from 'zod-i18n-map/locales/it/zod.json'

import plCommon from '@/assets/locales/pl/common.json';
import plZod from 'zod-i18n-map/locales/pl/zod.json'

import ptPTCommon from '@/assets/locales/pt-PT/common.json';
import ptBRCommon from '@/assets/locales/pt-BR/common.json';
import ptZod from 'zod-i18n-map/locales/pt/zod.json'

import esESCommon from '@/assets/locales/es-ES/common.json';
import esMXCommon from '@/assets/locales/es-MX/common.json';
import esZod from 'zod-i18n-map/locales/es/zod.json'

const LANGUAGE_STORAGE_KEY = '@octahub_language_override';

export const SUPPORTED_LANGUAGES = [
    { code: 'system', name: 'System' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'it', name: 'Italiano' },
    { code: 'pl', name: 'Polski' },
    { code: 'pt-PT', name: 'Português (Portugal)' },
    { code: 'pt-BR', name: 'Português (Brasil)' },
    { code: 'es-ES', name: 'Español (España)' },
    { code: 'es-MX', name: 'Español (México)' },
] as const;

const resources = {
    en: {
        common: enCommon,
        zod: enZod
    },
    de: {
        common: deCommon,
        zod: deZod
    },
    fr: {
        common: frCommon,
        zod: frZod
    },
    it: {
        common: itCommon,
        zod: itZod
    },
    pl: {
        common: plCommon,
        zod: plZod
    },
    pt: {
        zod: ptZod
    },
    'pt-PT': {
        common: ptPTCommon
    },
    'pt-BR': {
        common: ptBRCommon
    },
    es: {
        zod: esZod
    },
    'es-ES': {
        common: esESCommon
    },
    'es-MX': {
        common: esMXCommon
    }
}

const deviceLanguageTag = Localization.getLocales()[0]?.languageTag || 'en';

i18n
    .use(initReactI18next)
    .init({
        lng: deviceLanguageTag,
        fallbackLng: {
            'pt-BR': ['pt', 'en'],
            'pt-PT': ['pt', 'en'],
            pt: ['pt-PT', 'en'],
            'es-MX': ['es', 'en'],
            'es-ES': ['es', 'en'],
            es: ['es-ES', 'en'],
            default: ['en']
        },
        supportedLngs: ['en', 'de', 'fr', 'it', 'pl', 'pt', 'pt-PT', 'pt-BR', 'es', 'es-ES', 'es-MX'],
        nonExplicitSupportedLngs: true,
        ns: ['common', 'zod'],
        defaultNS: 'common',
        resources,
        interpolation: {
            escapeValue: false
        }
    });

export async function loadSavedLanguage() {
    try {
        const saved = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (saved && saved !== 'system') {
            await i18n.changeLanguage(saved);
        }
    } catch {
        // Ignore storage errors, keep device locale
    }
}

export async function changeLanguage(code: string) {
    try {
        if (code === 'system') {
            await AsyncStorage.removeItem(LANGUAGE_STORAGE_KEY);
            const tag = Localization.getLocales()[0]?.languageTag || 'en';
            await i18n.changeLanguage(tag);
        } else {
            await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, code);
            await i18n.changeLanguage(code);
        }
    } catch {
        // Ignore storage errors
    }
}

export default i18n;
